import { useEffect, useState } from 'react';
import { partiesApi, usersApi, hasFullScope, getStoredUser, type Party, type UserBrief, type UnattributedText, type HandoverRecord } from '../api/client.js';
import { ErrorBanner, SkeletonRows, TableEmpty } from '../components/States.js';
import { roleCan, permHint } from '../utils/permissions.js';

const TYPE_LABELS: Record<string, string> = {
  customer: '客户',
  supplier: '供应商',
  both: '客户+供应商',
};

const TYPE_COLORS: Record<string, string> = {
  customer: 'bg-blue-100 text-blue-700',
  supplier: 'bg-red-100 text-red-700',
  both: 'bg-purple-100 text-purple-700',
};

// 8.4.6 归属变更类型（负责人变更史展示）
const HANDOVER_REASON_LABELS: Record<string, { text: string; cls: string }> = {
  deactivation: { text: '停用交接', cls: 'bg-amber-100 text-amber-700' },
  manual: { text: '直接重指派', cls: 'bg-sky-100 text-sky-700' },
};

// 8.4.9 交接复核结论（本档案时间线只读展示，批注在成员页发起）
const HANDOVER_REVIEW_LABELS: Record<string, { text: string; cls: string }> = {
  confirmed: { text: '已确认', cls: 'bg-emerald-100 text-emerald-700' },
  corrected: { text: '已更正', cls: 'bg-sky-100 text-sky-700' },
  flagged: { text: '可疑待核', cls: 'bg-rose-100 text-rose-700' },
};

const emptyForm = { name: '', type: 'customer', contact: '', phone: '', email: '', taxId: '', note: '', ownerUserId: '' };

export default function Parties() {
  const canWrite = roleCan('party:write');
  // 8.4.2 自助建档：accountant 可新建（强制挂自己）并维护自持档案；他人/公开档案与治理仍限 REVIEW
  const canCreate = roleCan('party:create');
  const me = getStoredUser();
  const selfWriter = me?.role === 'accountant';
  const canOperate = (p: Party) => canWrite || (selfWriter && p.ownerUserId === me?.id);
  const opHint = (p: Party) => (canOperate(p) ? undefined : selfWriter ? '只能操作你负责的档案（公开或他人档案请联系管理员）' : permHint('party:write'));
  const fullScope = hasFullScope();
  const isAdmin = me?.role === 'admin'; // 8.4.6 负责人变更史仅 admin 可回查（GET /users/handovers 为 ADMIN_ONLY）
  const [users, setUsers] = useState<UserBrief[]>([]);
  const [parties, setParties] = useState<Party[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [kwInput, setKwInput] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState<string | null>(null); // null=新建
  const [origName, setOrigName] = useState(''); // 编辑时的原名称，用于判断是否改名
  const [backfill, setBackfill] = useState(false); // 改名时同步回填历史凭证 counterparty
  const [editAliases, setEditAliases] = useState<string[]>([]); // 编辑中档案的曾用名清单（⑧ 手工补录）
  const [aliasInput, setAliasInput] = useState('');
  const [ownerHistory, setOwnerHistory] = useState<HandoverRecord[]>([]); // 8.4.6 编辑中档案的负责人变更史（admin）
  // ⑪ 未归属对手方文本治理面板
  const [unattr, setUnattr] = useState<UnattributedText[]>([]);
  const [unattrOpen, setUnattrOpen] = useState(false);
  const [unattrLoading, setUnattrLoading] = useState(false);
  const [selText, setSelText] = useState('');
  const [attachTarget, setAttachTarget] = useState(''); // 挂别名目标档案 id
  const [form, setForm] = useState({ ...emptyForm });

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await partiesApi.list({
        type: typeFilter || undefined,
        keyword: kwInput.trim() || undefined,
        pageSize: 200,
      });
      setParties(res.parties);
      setTotal(res.total);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [typeFilter, kwInput]);

  // 8.4：全权角色（admin/senior）加载成员清单供负责人下拉
  useEffect(() => {
    if (!fullScope) return;
    usersApi.list().then(setUsers).catch(() => setUsers([]));
  }, [fullScope]);

  const loadUnattr = async () => {
    setUnattrLoading(true);
    try {
      setUnattr(await partiesApi.unattributed());
    } catch (e: any) {
      setError(e.message);
    } finally {
      setUnattrLoading(false);
    }
  };

  // 面板展开期间跟随列表刷新（改名/挂别名/建档后归属关系会变）
  useEffect(() => { if (unattrOpen) loadUnattr(); }, [unattrOpen, parties.length]);

  const openCreate = () => {
    setEditId(null);
    setOrigName('');
    setBackfill(false);
    setEditAliases([]);
    setAliasInput('');
    setOwnerHistory([]);
    setForm({ ...emptyForm });
    setShowForm(true);
  };

  const openEdit = (p: Party) => {
    setEditId(p.id);
    setOrigName(p.name);
    setBackfill(false);
    setEditAliases(p.aliases || []);
    setAliasInput('');
    setForm({
      name: p.name, type: p.type,
      contact: p.contact || '', phone: p.phone || '', email: p.email || '',
      taxId: p.taxId || '', note: p.note || '', ownerUserId: p.ownerUserId || '',
    });
    setShowForm(true);
    // 8.4.6 admin 打开编辑时加载该档案负责人变更史（其他角色无权读审计，置空）
    if (isAdmin) {
      usersApi.listHandovers({ partyId: p.id, limit: 50 }).then(setOwnerHistory).catch(() => setOwnerHistory([]));
    } else {
      setOwnerHistory([]);
    }
  };

  const doAddAlias = async () => {
    const v = aliasInput.trim();
    if (!editId || !v) return;
    setError('');
    try {
      const p = await partiesApi.addAlias(editId, v);
      setEditAliases(p.aliases || []);
      setAliasInput('');
      await load();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const doDropAlias = async (alias: string) => {
    if (!editId) return;
    setError('');
    try {
      const p = await partiesApi.removeAlias(editId, alias);
      setEditAliases(p.aliases || []);
      await load();
    } catch (err: any) {
      setError(err.message);
    }
  };

  // ⑪ 一键建档：以原文本为档案名（可改），改名时把原文本登记为曾用名
  const createWithText = async (text: string) => {
    setError('');
    const name = (window.prompt('新建往来单位档案名称（原文本将登记为曾用名）', text) || '').trim();
    if (!name) return;
    try {
      const p = await partiesApi.create({ name, type: 'customer' });
      if (name !== text) await partiesApi.addAlias(p.id, text);
      await load();
    } catch (err: any) {
      setError(err.message);
    }
  };

  // ⑪ 挂别名并归一：登记曾用名后把该文本凭证改写为档案现名（同事务）
  const attachToParty = async (text: string) => {
    if (!attachTarget) { setError('请先选择要归属的往来单位档案'); return; }
    const target = parties.find((p) => p.id === attachTarget);
    if (!window.confirm(`将凭证文本「${text}」归入档案「${target?.name ?? attachTarget}」？\n会登记为曾用名并把 ${unattr.find((u) => u.text === text)?.entryCount ?? '?'} 张历史凭证文本改写为档案现名。`)) return;
    setError('');
    try {
      const res = await partiesApi.attachAliasNormalize(attachTarget, text);
      window.alert(`归一完成：${(res as any).normalized ?? 0} 张凭证文本已改写为「${res.name}」。`);
      setSelText('');
      await load();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      const data: {
        name: string; type: Party['type']; contact?: string; phone?: string; email?: string; taxId?: string; note?: string;
        ownerUserId?: string | null;
      } = {
        name: form.name.trim(),
        type: form.type as Party['type'],
        contact: form.contact.trim() || undefined,
        phone: form.phone.trim() || undefined,
        email: form.email.trim() || undefined,
        taxId: form.taxId.trim() || undefined,
        note: form.note.trim() || undefined,
      };
      // 8.4 仅全权角色可指派/清空负责人；新建时「公开」显式传 null（否则服务端会默认挂创建人）
      if (fullScope) data.ownerUserId = form.ownerUserId.trim() || null;
      if (editId) {
        const renamed = form.name.trim() !== origName;
        const res = await partiesApi.update(editId, { ...data, backfill: renamed && backfill });
        if (renamed && backfill) {
          window.alert(`改名成功，已同步回填 ${(res as any).backfilled ?? 0} 张历史凭证的对手方文本。`);
        } else if (renamed) {
          window.alert(`改名成功。旧名「${origName}」已登记为曾用名，仍携旧文本的历史凭证会继续关联该档案联系方式（未回填凭证文本）。`);
        }
      } else {
        await partiesApi.create(data);
      }
      setShowForm(false);
      setEditId(null);
      await load();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const remove = async (p: Party) => {
    if (!window.confirm(`确认停用往来单位「${p.name}」？历史凭证的对手方文本不受影响。`)) return;
    try {
      await partiesApi.remove(p.id);
      await load();
    } catch (err: any) {
      // 409：尚有未结余额 → 二次确认后强制停用
      if (err.status === 409 && err.code === 'PARTY_HAS_OPEN_AGING') {
        if (window.confirm(err.message)) {
          try {
            await partiesApi.remove(p.id, true);
            await load();
          } catch (err2: any) {
            setError(err2.message);
          }
        }
        return;
      }
      setError(err.message);
    }
  };

  const set = (patch: Partial<typeof form>) => setForm((f) => ({ ...f, ...patch }));

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-2xl font-bold text-slate-800">往来单位档案</h2>
        <button
          onClick={showForm ? () => setShowForm(false) : openCreate}
          disabled={!canCreate && !showForm}
          title={permHint('party:create')}
          className="btn btn-primary"
        >
          {showForm ? '取消' : '+ 新增往来单位'}
        </button>
      </div>

      {error && (
        <div className="mb-4">
          <ErrorBanner message={error} onRetry={error.startsWith('加载') ? load : undefined} />
        </div>
      )}

      {showForm && (
        <form onSubmit={submit} className="mb-6 p-4 bg-white rounded-xl shadow-sm">
          <h3 className="text-sm font-bold text-slate-700 mb-3">{editId ? '编辑往来单位' : '新增往来单位'}</h3>
          <div className="grid grid-cols-4 gap-3 items-end">
            <div>
              <label className="block text-xs text-slate-500 mb-1">名称 *</label>
              <input value={form.name} onChange={(e) => set({ name: e.target.value })} placeholder="如：A客户、恒力办公用品" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" required maxLength={60} />
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1">类型</label>
              <select value={form.type} onChange={(e) => set({ type: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm">
                <option value="customer">客户</option>
                <option value="supplier">供应商</option>
                <option value="both">客户+供应商</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1">联系人</label>
              <input value={form.contact} onChange={(e) => set({ contact: e.target.value })} placeholder="如：王经理" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1">电话</label>
              <input value={form.phone} onChange={(e) => set({ phone: e.target.value })} placeholder="如：138-0000-0000" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1">邮箱</label>
              <input value={form.email} onChange={(e) => set({ email: e.target.value })} placeholder="可选" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1">税号</label>
              <input value={form.taxId} onChange={(e) => set({ taxId: e.target.value })} placeholder="可选" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div className="col-span-2">
              <label className="block text-xs text-slate-500 mb-1">备注</label>
              <input value={form.note} onChange={(e) => set({ note: e.target.value })} placeholder="如：账期 30 天 / 对接注意事项" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
            </div>
            {fullScope && (
              <div className="col-span-2">
                <label className="block text-xs text-slate-500 mb-1">负责人<span className="text-slate-400">（8.4 数据归属：指定后仅该成员与管理员可基于此档案录入/查看；负责人变更会记入成员页「交接审计轨迹」）</span></label>
                <select value={form.ownerUserId} onChange={(e) => set({ ownerUserId: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white">
                  <option value="">公开（无主，全员可见）</option>
                  {users.filter((u) => u.isActive).map((u) => (
                    <option key={u.id} value={u.id}>{u.displayName}（{u.role}）</option>
                  ))}
                </select>
              </div>
            )}
          </div>
          <div className="mt-3 flex items-center gap-2">
            <button type="submit" disabled={!canCreate} title={permHint('party:create')} className="btn btn-primary btn-sm">
              {editId ? '保存修改' : '创建'}
            </button>
            {!fullScope && canCreate && (
              <p className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-lg px-2 py-1">新建档案将自动挂你为负责人（自助建档）；需指派他人或设为公开请联系管理员</p>
            )}
            {editId && form.name.trim() !== origName && (
              <label className="flex items-center gap-1.5 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1.5 cursor-pointer">
                <input type="checkbox" checked={backfill} onChange={(e) => setBackfill(e.target.checked)} className="accent-amber-600" />
                已改名：同步回填历史凭证的对手方文本（原「{origName}」）
              </label>
            )}
            <p className="text-xs text-slate-400">名称与凭证「往来对手方」文本按名称关联；同名档案唯一</p>
          </div>
          {editId && (
            <div className="mt-3 pt-3 border-t border-slate-100">
              <div className="text-xs text-slate-500 mb-1.5">曾用名 / 别名<span className="text-slate-400">（历史凭证携旧名仍可关联本档案联系方式，可手工补录修复脱节改名）</span></div>
              <div className="flex flex-wrap items-center gap-1.5 mb-2">
                {editAliases.length === 0 ? (
                  <span className="text-xs text-slate-400">暂无曾用名</span>
                ) : (
                  editAliases.map((al) => (
                    <span key={al} className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 flex items-center gap-1">
                      {al}
                      <button type="button" onClick={() => doDropAlias(al)} disabled={!canCreate} title="移除该曾用名" className="text-slate-400 hover:text-red-500 disabled:cursor-not-allowed">✕</button>
                    </span>
                  ))
                )}
              </div>
              <div className="flex items-center gap-2">
                <input value={aliasInput} onChange={(e) => setAliasInput(e.target.value)} placeholder="补录旧名，如改名前的名称" maxLength={60} className="border border-slate-300 rounded-lg px-3 py-1.5 text-sm w-64" />
                <button type="button" onClick={doAddAlias} disabled={!canCreate || !aliasInput.trim()} title={permHint('party:create')} className="btn btn-sm bg-white border border-slate-200 text-slate-700 hover:bg-slate-100 disabled:text-slate-400 disabled:cursor-not-allowed">+ 补录曾用名</button>
              </div>
            </div>
          )}
          {editId && isAdmin && (
            <div className="mt-3 pt-3 border-t border-slate-100">
              <div className="text-xs text-slate-500 mb-1.5">🧾 负责人变更史<span className="text-slate-400">（本档案历次归属变更的合规留痕，按时间倒序；变更负责人会新增一条）</span></div>
              {ownerHistory.length === 0 ? (
                <span className="text-xs text-slate-400">暂无负责人变更记录</span>
              ) : (
                <ul className="space-y-1 max-h-40 overflow-y-auto pr-1">
                  {ownerHistory.map((h) => {
                    const rl = HANDOVER_REASON_LABELS[h.reason] || HANDOVER_REASON_LABELS.manual;
                    return (
                      <li key={h.id} className="text-xs text-slate-600 flex items-center flex-wrap gap-x-1.5 gap-y-0.5">
                        <span className="text-slate-400">{String(h.createdAt).slice(0, 16).replace('T', ' ')}</span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded ${rl.cls}`}>{rl.text}</span>
                        <span>{h.fromName || '公开'}</span>
                        <span className="text-slate-400">→</span>
                        <span>{h.toName || '公开池'}</span>
                        <span className="text-slate-400">· 执行人 {h.operatorName || '—'}</span>
                        {h.review && (() => { const rv = HANDOVER_REVIEW_LABELS[h.review.verdict] || HANDOVER_REVIEW_LABELS.confirmed; return <span className={`text-[10px] px-1.5 py-0.5 rounded ${rv.cls}`} title={h.review.note || undefined}>复核·{rv.text}{h.review.note ? '：' + h.review.note : ''}</span>; })()}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          )}
        </form>
      )}

      <div className="flex items-center gap-3 mb-3">
        <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="border border-slate-300 rounded-lg px-3 py-1.5 text-sm bg-white">
          <option value="">全部类型</option>
          <option value="customer">客户</option>
          <option value="supplier">供应商</option>
          <option value="both">客户+供应商</option>
        </select>
        <input
          value={kwInput}
          onChange={(e) => setKwInput(e.target.value)}
          placeholder="搜索名称 / 联系人 / 电话"
          className="border border-slate-300 rounded-lg px-3 py-1.5 text-sm w-64"
        />
        <span className="text-xs text-slate-400">共 {total} 个往来单位</span>
      </div>

      {/* ⑪ 未归属对手方文本治理面板 */}
      <div className="mb-3 bg-white rounded-xl shadow-sm overflow-hidden">
        <button
          type="button"
          onClick={() => setUnattrOpen((v) => !v)}
          className="w-full flex items-center justify-between px-4 py-2.5 text-sm hover:bg-slate-50"
        >
          <span className="font-medium text-slate-700">
            🔍 未归属对手方文本
            {unattrLoading ? <span className="ml-2 text-xs text-slate-400">扫描中…</span> : (
              <span className={`ml-2 text-xs px-1.5 py-0.5 rounded-full ${unattr.length ? 'bg-amber-100 text-amber-700' : 'bg-emerald-50 text-emerald-600'}`}>
                {unattr.length ? `${unattr.length} 条待治理` : '全部已归属'}
              </span>
            )}
            <span className="ml-2 text-xs font-normal text-slate-400">凭证携此文本但无启用档案匹配（脱节改名/录入别名未补录/新对手方）</span>
          </span>
          <span className="text-xs text-slate-400">{unattrOpen ? '收起 ▲' : '展开 ▼'}</span>
        </button>
        {unattrOpen && (
          <div className="border-t border-slate-100">
            {unattr.length === 0 && !unattrLoading ? (
              <p className="px-4 py-3 text-xs text-slate-400">没有未归属的对手方文本（筛选条件不影响扫描口径）</p>
            ) : (
              <div className="max-h-56 overflow-auto">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 text-slate-500 text-left sticky top-0">
                    <tr>
                      <th className="px-4 py-1.5 w-8"></th>
                      <th className="px-2 py-1.5">凭证文本</th>
                      <th className="px-2 py-1.5">涉及凭证</th>
                      <th className="px-2 py-1.5">往来净额</th>
                      <th className="px-2 py-1.5 text-right">治理操作</th>
                    </tr>
                  </thead>
                  <tbody>
                    {unattr.map((u) => (
                      <tr key={u.text} className="border-t border-slate-50">
                        <td className="px-4 py-1.5">
                          <input type="radio" name="unattr-sel" checked={selText === u.text} onChange={() => setSelText(u.text)} />
                        </td>
                        <td className="px-2 py-1.5 font-medium text-slate-700">{u.text}</td>
                        <td className="px-2 py-1.5">{u.entryCount} 张</td>
                        <td className="px-2 py-1.5 font-mono">{u.openAmount.toFixed(2)}</td>
                        <td className="px-2 py-1.5 text-right whitespace-nowrap">
                          <button onClick={() => createWithText(u.text)} disabled={!canWrite} title={permHint('party:write')} className="text-blue-600 hover:underline mr-3">➕ 建档</button>
                          <select
                            value={attachTarget}
                            onChange={(e) => setAttachTarget(e.target.value)}
                            disabled={!canWrite || selText !== u.text}
                            className="border border-slate-200 rounded px-1.5 py-0.5 text-xs bg-white max-w-[9rem] disabled:opacity-50 mr-1"
                          >
                            <option value="">挂别名到…</option>
                            {parties.filter((p) => p.isActive).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                          </select>
                          <button onClick={() => attachToParty(u.text)} disabled={!canWrite || selText !== u.text || !attachTarget} title={selText !== u.text ? '先选中左侧单选框' : permHint('party:write')} className="text-amber-700 hover:underline disabled:text-slate-300 disabled:cursor-not-allowed">归一</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-left">
            <tr>
              <th className="px-4 py-2.5">名称</th>
              <th className="px-4 py-2.5">类型</th>
              <th className="px-4 py-2.5">联系人</th>
              <th className="px-4 py-2.5">电话</th>
              <th className="px-4 py-2.5">邮箱 / 税号</th>
              {fullScope && <th className="px-4 py-2.5">负责人</th>}
              <th className="px-4 py-2.5">备注</th>
              <th className="px-4 py-2.5 text-right">操作</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <SkeletonRows colSpan={fullScope ? 8 : 7} />
            ) : parties.length === 0 ? (
              <TableEmpty colSpan={fullScope ? 8 : 7} icon="🤝" title="还没有往来单位档案" hint="建档后凭证录入可选名建议，账龄按档案展示联系人" />
            ) : (
              parties.map((p) => (
                <tr key={p.id} className={`border-t border-slate-100 ${p.isActive ? '' : 'opacity-50'}`}>
                  <td className="px-4 py-2.5 font-medium text-slate-800">
                    {p.name}
                    {!p.isActive && <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">已停用</span>}
                    {p.aliases && p.aliases.length > 0 && (
                      <div className="mt-0.5 text-[11px] font-normal text-slate-400" title="曾用名（历史凭证仍按此名关联联系方式）">
                        曾用名：{p.aliases.join('、')}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-2.5">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${TYPE_COLORS[p.type] || 'bg-slate-100 text-slate-600'}`}>{TYPE_LABELS[p.type] || p.type}</span>
                  </td>
                  <td className="px-4 py-2.5 text-slate-600">{p.contact || '—'}</td>
                  <td className="px-4 py-2.5 text-slate-600 font-mono text-xs">{p.phone || '—'}</td>
                  <td className="px-4 py-2.5 text-slate-500 text-xs">
                    {p.email || ''}{p.email && p.taxId ? ' · ' : ''}{p.taxId || ''}
                    {!p.email && !p.taxId && '—'}
                  </td>
                  <td className="px-4 py-2.5 text-slate-500 text-xs max-w-[14rem] truncate" title={p.note || ''}>{p.note || '—'}</td>
                  {fullScope && (
                    <td className="px-4 py-2.5 text-xs">
                      {p.ownerUserId
                        ? <span className="text-slate-700">{p.ownerName || '已指派'}</span>
                        : <span className="text-slate-400">公开</span>}
                    </td>
                  )}
                  <td className="px-4 py-2.5 text-right whitespace-nowrap">
                    <button onClick={() => openEdit(p)} disabled={!canOperate(p)} title={opHint(p)} className="text-xs text-blue-600 hover:underline mr-3">
                      编辑
                    </button>
                    {p.isActive && (
                      <button onClick={() => remove(p)} disabled={!canOperate(p)} title={opHint(p)} className="text-xs text-red-500 hover:underline">
                        停用
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
